import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Music, Video, FileText, Image, Download, ExternalLink, Play } from 'lucide-react';

const PracticeMaterialsTab = ({ practiceMaterials }) => {
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-6 h-6" />;
      case 'video': return <Video className="w-6 h-6" />;
      case 'audio': return <Music className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const getFileTypeColor = (fileType) => {
    switch (fileType) {
      case 'image': return 'bg-blue-500/20 text-blue-400';
      case 'video': return 'bg-purple-500/20 text-purple-400';
      case 'audio': return 'bg-green-500/20 text-green-400';
      default: return 'bg-amber-500/20 text-amber-400';
    }
  };

  const handleDownload = (material) => {
    const link = document.createElement('a');
    link.href = material.file_url;
    link.download = material.file_name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
          <Music className="w-5 h-5 text-green-400" />
          Practice Materials
        </h3>
        <p className="text-sm text-white/50">
          {practiceMaterials.length} files available
        </p>
      </div>

      {practiceMaterials.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-8 text-center">
            <Music className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2 text-white">No practice materials yet</h3>
            <p className="text-white/50">Your teachers will share music, videos, and documents here for you to practice at home.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {practiceMaterials.map(material => (
            <Card key={material.id} className="bg-[#111] border-[#333] hover:border-green-500/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getFileTypeColor(material.file_type)}`}>
                    {getFileIcon(material.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white truncate">{material.file_name}</h4>
                    {material.description && (
                      <p className="text-sm text-white/50 truncate">{material.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {material.is_general ? (
                        <Badge className="bg-white/10 text-white/70 text-xs">All Classes</Badge>
                      ) : (
                        material.class_names?.map((name, idx) => (
                          <Badge key={idx} className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                            {name}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-white/10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(material)}
                    className="flex-1 border-green-500/30 text-green-400 hover:bg-green-500/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(material.file_url, '_blank')}
                    className="flex-1 border-[#333] text-white hover:bg-white/10"
                  >
                    {material.file_type === 'video' || material.file_type === 'audio' ? (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Play
                      </>
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-green-500/10 border-green-500/30">
        <CardContent className="p-4">
          <p className="text-white/70 text-sm">
            <strong className="text-green-400">Tip:</strong> Download materials for offline access during practice sessions. 
            Videos and audio files can be played directly by clicking "Play".
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PracticeMaterialsTab;
